import ContentBlocks from "../components/content/ContentBlocks";
import CaseStudyVideo from "../components/content/CaseStudyVideo";
import { ArrowLeft } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";
import { routes } from "../data/site";
import { cx } from "../lib/cx";
import { imageKey, isSupportingMark } from "../lib/content-blocks";
import { assetUrl, imageProps, localHref } from "../lib/links";
import { techFor } from "../data/tech";

/**
 * Split captured case-study blocks at each h2 (About, Challenge, Solution,
 * Technology Stacks, Results). Repeated images and passages are dropped; photos are
 * pulled out of each section so they can sit beside its text.
 */
function sectionsFrom(blocks, path) {
  const canonicalPath = path?.replace(/\d+\/$/, "");
  const seen = new Set();
  const sections = [{ heading: null, blocks: [], photos: [] }];
  for (const block of blocks) {
    if (block.type === "h1" || block.type === "embed") continue;
    if (block.type === "rich" && !block.html.replace(/<[^>]+>|&nbsp;|\s/g, ""))
      continue;
    // The live pages carry desktop and mobile copies of some photos and
    // quotes; keep the first of each (short lines may legitimately repeat).
    const text = (block.html || block.text || "")
      .replace(/<[^>]+>|\s+/g, " ")
      .trim();
    const key =
      block.type === "image"
        ? imageKey(block.src)
        : text.length > 40 && `text:${text}`;
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    if (block.type === "h2")
      sections.push({ heading: block.text, blocks: [], photos: [] });
    let section = sections[sections.length - 1];
    if (block.type === "h2") continue;
    const isTech =
      block.type === "image" &&
      /technolog/i.test(section.heading || "") &&
      techFor(block.src);
    // Captured testimonials follow the stack without their own heading.
    // Give that content a separate section rather than treating it as technology.
    if (/technolog/i.test(section.heading || "") && !isTech) {
      section = {
        heading: "From the field",
        blocks: [],
        photos: [],
        feedback: true,
      };
      sections.push(section);
    }
    if (block.type === "image" && !isSupportingMark(block.src) && !isTech)
      section.photos.push(block);
    else
      section.blocks.push(
        block.type === "rich"
          ? {
              ...block,
              html: block.html
                .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
                .replace(
                  "<p>https://loadtraxx.com/</p>",
                  '<p><a href="https://loadtraxx.com/">Visit LoadTraxx</a></p>',
                )
                .replace(
                  canonicalPath?.startsWith("/loadtraxx-")
                    ? /<p>([A-Z][^<:]{2,45}:)(?=\s)/g
                    : /$^/,
                  "<p><strong>$1</strong>",
                ),
            }
          : block,
      );
  }
  const populated = sections.filter((s) => s.blocks.length || s.photos.length);
  // Older captures put the product screenshot before its corresponding heading.
  if (
    canonicalPath ===
    "/mj-electric-streamlining-operations-through-business-process-integration/"
  ) {
    const screenshots = populated.flatMap((s) => s.photos);
    populated.forEach((s) => {
      s.photos = [];
    });
    ["T&M Billing", "Safety Assessments", "Accruals & Forecasting"].forEach(
      (heading, i) => {
        const section = populated.find((s) => s.heading?.includes(heading));
        if (section && screenshots[i]) section.photos = [screenshots[i]];
      },
    );
    populated[0].blocks.unshift({
      type: "h3",
      text: "Customized Web-Based Platform",
    });
  }
  if (
    canonicalPath ===
      "/oem-enhancing-efficiency-with-the-lean-manufacturing-assistant/" &&
    populated[0]?.photos.length &&
    !populated[0].blocks.length
  ) {
    const narrative = populated.splice(1, 1)[0];
    populated[0].blocks = [
      { type: "h3", text: narrative.heading },
      ...narrative.blocks,
    ];
  }
  const lead = populated[0];
  if (lead) {
    lead.logos = lead.blocks.filter(
      (block) => block.type === "image" && isSupportingMark(block.src),
    );
    lead.blocks = lead.blocks.filter((block) => !lead.logos.includes(block));
    // The LoadTraxx wordmark was captured under a generic image filename.
    if (canonicalPath?.startsWith("/loadtraxx-")) {
      const logo = lead.photos.find((block) =>
        /Untitled-design-2024-05-01T140751/.test(block.src),
      );
      if (logo) {
        lead.logos.push({ ...logo, alt: "LoadTraxx" });
        lead.photos = lead.photos.filter((block) => block !== logo);
      }
    }
    // All three M. J. Electric stories share the same client mark.
    if (canonicalPath?.startsWith("/mj-electric-") && !lead.logos.length)
      lead.logos.push({
        src: assetUrl("Web-4.png"),
        alt: "M. J. Electric",
      });
  }
  return populated;
}

function ClientLogos({ logos, title }) {
  return (
    <div className="flex flex-col items-center justify-center gap-8">
      {logos.map((logo) => {
        const padded = /Web-4(?:-\d+x\d+)?\.png/.test(logo.src);
        const loadTraxx = /Untitled-design-2024-05-01T140751/.test(logo.src);
        return (
          <figure
            key={logo.src}
            className={cx(
              "flex w-full max-w-80 items-center justify-center",
              padded && "aspect-square max-w-64 overflow-hidden",
              loadTraxx && "rounded-brand bg-ink px-6 py-8",
            )}
          >
            <img
              {...imageProps(localHref(logo.src), padded ? "512px" : "320px")}
              alt={logo.alt || `${title} client logo`}
              loading="lazy"
              className={cx(
                "h-auto w-full object-contain",
                padded ? "scale-[1.9]" : "max-h-64",
              )}
            />
          </figure>
        );
      })}
    </div>
  );
}

const isScreenshot = (src) =>
  /screenshot-2022|dispatch-log|omega-intro|tasks-in-pipline|michels_international/i.test(
    src,
  );

function Photos({ photos, title, feedback }) {
  return (
    <div className="grid gap-5">
      {photos.map((block) => {
        const screenshot = isScreenshot(block.src);
        return (
          <figure key={block.src} className="min-w-0">
            <img
              {...imageProps(
                localHref(block.src),
                "(max-width: 1024px) 100vw, 600px",
              )}
              alt={
                block.alt ||
                `${title} — ${screenshot ? "application screenshot" : "photo"}`
              }
              loading="lazy"
              className={cx(
                "mx-auto h-auto w-auto max-w-full object-contain",
                screenshot
                  ? "max-h-[560px] rounded-brand border border-line bg-white p-1.5 shadow-soft"
                  : photos.length > 1
                    ? "max-h-[320px] rounded-brand"
                    : "max-h-[460px] rounded-brand shadow-soft",
                feedback && /nicparish/i.test(block.src) && "max-w-48",
                /Challenge-Page-Pic|Xeratec-Loadtraxx-case-study/i.test(
                  block.src,
                ) && "bg-ink p-5",
              )}
            />
            {screenshot && (
              <a
                href={localHref(block.src)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-sm font-semibold text-blue-deep underline underline-offset-4"
              >
                View full-size image{" "}
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            )}
          </figure>
        );
      })}
    </div>
  );
}

/**
 * Case study body as full-width sections that alternate text and photos side
 * to side (as on the live site), on alternating white and light-grey bands.
 * The Results section sits on dark slate.
 */
export default function CaseStudySections({
  blocks,
  title,
  results,
  videos,
  path,
  backHref = localHref(routes.caseStudies),
}) {
  const sections = sectionsFrom(blocks, path);
  let photoSide = 0;
  return (
    <>
      <nav
        aria-label="Case study navigation"
        className="z-30 border-b md:sticky md:top-[71px] border-line bg-white/95 px-4 py-2.5 backdrop-blur-md sm:px-6"
      >
        <div className="mx-auto flex max-w-280 flex-wrap items-center gap-x-8 gap-y-1">
          <a
            href={backHref}
            className="inline-flex min-h-10 items-center gap-2 font-bold text-blue-deep transition-colors hover:text-ink"
          >
            <ArrowLeft size={17} aria-hidden="true" /> Back to Results
          </a>
          <div className="flex flex-wrap gap-x-6 gap-y-0 text-sm font-semibold text-muted">
            {sections.map(
              (section, i) =>
                section.heading && (
                  <a
                    key={i}
                    href={`#case-section-${i}`}
                    className="inline-flex min-h-10 items-center hover:text-blue-deep"
                  >
                    {i === 0
                      ? "Overview"
                      : /about/i.test(section.heading)
                        ? "About the client"
                        : /challenge/i.test(section.heading)
                          ? "Challenge"
                          : /solution/i.test(section.heading)
                            ? "Solution"
                            : /technolog/i.test(section.heading)
                              ? "Technology"
                              : /result/i.test(section.heading)
                                ? "Results"
                                : /strategic vision/i.test(section.heading)
                                  ? "Strategic vision"
                                  : section.heading}
                  </a>
                ),
            )}
            {videos.length > 0 && (
              <a
                href="#case-videos"
                className="inline-flex min-h-10 items-center hover:text-blue-deep"
              >
                Videos
              </a>
            )}
          </div>
        </div>
      </nav>
      {sections.map((section, i) => {
        const dark = /result/i.test(section.heading || "");
        const hasPhotos = section.photos.length > 0;
        const hasLogos = section.logos?.length > 0;
        const photosFirst = hasPhotos && photoSide++ % 2 === 1;
        const isLead = i === 0;
        return (
          <section
            key={i}
            id={`case-section-${i}`}
            className={cx(
              "case-section md:scroll-mt-12 px-4 py-14 sm:px-6 md:py-20",
              dark ? "blueprint" : i % 2 ? "bg-surface" : "bg-white",
            )}
          >
            <div
              className={cx(
                "mx-auto",
                hasLogos
                  ? "grid max-w-280 grid-cols-1 items-center gap-10 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-16"
                  : hasPhotos
                    ? "grid max-w-280 grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
                    : "max-w-3xl",
              )}
            >
              <div
                className={cx(
                  "min-w-0",
                  (photosFirst || hasLogos) && "lg:order-2",
                )}
              >
                {isLead && results && (
                  <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {results.map(({ value, label }) => (
                      <div
                        key={label}
                        className="rounded-brand border-t-[3px] border-yellow bg-ink p-6"
                      >
                        <div className="font-display text-4xl text-yellow md:text-5xl">
                          {value}
                        </div>
                        <div className="mt-2 font-medium text-on-dark">
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {section.heading && (
                  <h2
                    className={cx(
                      "font-display mb-6 text-[30px] uppercase md:text-[38px]",
                      dark ? "text-white" : "text-slate",
                    )}
                  >
                    {section.heading}
                  </h2>
                )}
                <div
                  className={cx(
                    dark && "prose-dark",
                    isLead && "prose-lead",
                    section.feedback && "case-feedback",
                  )}
                >
                  <ContentBlocks
                    blocks={section.blocks}
                    title={title}
                    tech={/technolog/i.test(section.heading || "")}
                  />
                </div>
              </div>
              {hasLogos && (
                <div className="min-w-0 lg:order-1">
                  <ClientLogos logos={section.logos} title={title} />
                </div>
              )}
              {hasPhotos && (
                <div className={cx("min-w-0", photosFirst && "lg:order-1")}>
                  <Photos
                    photos={section.photos}
                    title={title}
                    feedback={section.feedback}
                  />
                </div>
              )}
            </div>
          </section>
        );
      })}
      {videos.length > 0 && (
        <section
          id="case-videos"
          className="md:scroll-mt-12 bg-white px-4 py-14 sm:px-6 md:py-20"
        >
          <div className="mx-auto max-w-280">
            <SectionHeading
              eyebrow="See it in action"
              title="Watch the *story.*"
              className="mb-8"
            />
            <div
              className={cx(
                "grid grid-cols-1 gap-6",
                videos.length > 1 ? "md:grid-cols-2" : "max-w-3xl",
              )}
            >
              {videos.map((url) => (
                <CaseStudyVideo
                  key={url}
                  url={url}
                  title={`${title} video ${videos.indexOf(url) + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
